import React from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, Info, ShieldCheck, ShieldWarning, Warning} from '../../icons';
import {
    ANALYTICS_CHANNEL,
    CHANNEL_INFO_BY_CHANNEL,
    CHANNEL_TYPE_PAID_MEDIA,
} from '../../channels/channels';
import {isFixedInTheSpreadsheet} from '../../channels/adAccountErrorKinds';
import {EditClientAdAccountModalButton} from '../ad_accounts/EditClientAdAccountModalButton';
import {HistoricalSyncRow} from './HistoricalSyncRow';
import {accountConversionLabel, channelInfo, normalizeChannel} from './conversionLabels';
import {LOCALE} from './panelFormatters';
import {anchorAttr} from '../../../guides/anchors';
import {
    DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_ONLY_COST,
    DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_BUDGET,
    DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_COST,
    DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_COST_AND_BUDGET,
    DAILY_LIMIT_TYPES_WATCHING_BUDGET,
} from '../../../daily_limits';
import './DailyLimits.scss';

// Un límite diario es plata exacta que alguien escribió a mano: se muestra completo ("1.111.111"),
// no compactado ("1,1M"). El resto del panel compacta porque son totales que se leen de un vistazo;
// esto es un SETTING y el usuario tiene que poder verificarlo contra lo que cargó. El locale sale
// de `panelFormatters` — dos constantes para el mismo formato de plata se desincronizan el día que
// alguien internacionalice los montos.
function formatExactMoney(currency, amount) {
    if (amount === null || amount === undefined || amount === '') return null;
    const value = Number(amount);
    if (!Number.isFinite(value)) return null;
    const number = value.toLocaleString(LOCALE, {maximumFractionDigits: 0});
    return currency ? `${currency} ${number}` : number;
}

/** Si el canal maneja límite diario (paid media + Sheets; GA4, GSC y tiendas no). */
export function supportsDailyLimit(channel) {
    const info = CHANNEL_INFO_BY_CHANNEL[channel];
    return !!(info && info.dailyLimitEnabled);
}

/**
 * Si una cuenta caída se puede recuperar con el flujo de reconexión OAuth de la pantalla de
 * conexiones: paid media y GA4, sí; tiendas y hojas de cálculo, no.
 *
 * MISMO criterio que `ClientAdAccountRow.supportsOAuthReconnect` en la pantalla clásica. Importa
 * porque este render dejó de ser sólo de canales con límite diario: `PlatformsBox` mezcla las
 * tiendas con las cuentas publicitarias en un único mapa por canal, así que sin el gate una tienda
 * caída mandaba al usuario a buscar un botón "Reconectar" que en esa fila no existe.
 */
export function supportsReconnect(channel) {
    const normalized = normalizeChannel(channel);
    const info = CHANNEL_INFO_BY_CHANNEL[normalized];
    return !!info && (info.channelType === CHANNEL_TYPE_PAID_MEDIA || normalized === ANALYTICS_CHANNEL);
}

/**
 * El detalle por cuenta que `PlatformsBox` monta en la fila de un canal conectado, o `null` cuando
 * ese canal no tiene ninguna.
 *
 * **La decisión vive acá y no adentro del componente**: un elemento React es truthy AUNQUE el
 * componente devuelva `null` al renderizarse (el `null` pasa al renderizar el hijo, no al crear el
 * elemento). Devolviendo `<AccountRows/>` siempre, `PlatformsBox` creería que hay detalle para
 * todos los canales, marcaría la fila como `-stacked` y **nunca** caería a su resumen
 * "Nombre (ID: …)": un canal del plan sin cuentas quedaría con la fila literalmente vacía.
 */
export function renderAccountRows(channel, accounts, onEdited, syncByAdAccountId) {
    if (!accounts || !accounts.length) return null;
    return <AccountRows channel={channel} accounts={accounts} onEdited={onEdited}
                        syncByAdAccountId={syncByAdAccountId}/>;
}

/**
 * El progreso del histórico de una cuenta: el del refresco en vivo si ya llegó uno, y si no el que
 * vino en el agregado.
 *
 * Las dos fuentes tienen el MISMO shape a propósito (las arma el mismo servicio del backend), así
 * que acá no hay adaptador: sólo la regla de cuál gana. El refresco manda porque es más nuevo, y su
 * AUSENCIA también es información — una cuenta que terminó desaparece del refresco, y la fila tiene
 * que dejar de mostrarse aunque el agregado, que se pidió hace diez minutos, todavía la traiga.
 */
function syncOf(account, syncByAdAccountId) {
    if (syncByAdAccountId) {
        return syncByAdAccountId[String(account.ad_account_id)] || null;
    }
    return account.historical_sync || null;
}

function hasDailyLimit(account) {
    return account.daily_limit_type !== null && account.daily_limit_type !== undefined
        && account.daily_limit_currency !== null && account.daily_limit_currency !== undefined
        && account.daily_limit_value !== null && account.daily_limit_value !== undefined;
}

// El límite se pasó HOY: es la alarma para la que se configuró, así que la fila lo dice sin hover.
// El clásico compara además contra el presupuesto actual para adivinar si un límite recién editado
// sigue excedido; acá alcanza con el hecho ("hoy saltó"), que es lo que el usuario necesita saber.
function exceededToday(account) {
    return !!account.daily_limit_last_exceed_notification_is_today;
}

/**
 * Las fuentes del cliente, cuenta por cuenta, dentro de la card de Plataformas del Panel.
 *
 * Nació como la tabla de límites diarios (card Fa9hRD9I, que trajo al panel lo único que vivía en
 * Monitoreo) y se amplió con el resto de lo que define a una fuente —su moneda, qué conversión se le
 * lee y si Dashbo la sigue pudiendo leer— a partir del mockup v2 de Pablo. No cuesta una query:
 * **todo esto ya viajaba** en `panel.platforms.client_ad_accounts`, que es el mismo presenter que
 * sirve la pantalla de conexiones.
 *
 * Vive DENTRO de `PlatformsBox` y no como una tabla suelta a propósito: ese componente es el que
 * muestra también los canales que la agencia **todavía no conectó**, que es la mitad del valor de la
 * sección. Una tabla sólo de lo conectado contestaría "está todo bien" en un cliente al que le falta
 * la mitad de las fuentes.
 *
 * Tres decisiones de la ronda anterior que se conservan:
 * - El ⓘ explica QUÉ es un límite diario antes de pedir que se configure, y vive en la fila —donde
 *   aparece la duda—, no en un encabezado.
 * - "Sin límite" va en gris, NO en rojo como en el clásico: no tener límite no es un error, y un
 *   rojo que está siempre deja de significar algo.
 * - El presupuesto diario que la cuenta tiene hoy cargado va en el TOOLTIP: es con lo que se elige
 *   un monto razonable, pero no merece una columna (el mockup v2 le da una; sigue en el tooltip).
 */
export function AccountRows({channel, accounts, onEdited, syncByAdAccountId}) {
    const {t} = useTranslation();

    if (!accounts || !accounts.length) return null;

    const info = CHANNEL_INFO_BY_CHANNEL[channel] || {};
    // GA4, tiendas y demás no manejan límite diario: su fila muestra todo lo otro igual, sin la
    // pastilla. Antes esos canales caían al resumen "Nombre (ID: …)" y no decían ni su moneda ni si
    // seguían siendo accesibles.
    const limitApplies = !!info.dailyLimitEnabled;

    const scopeLabel = (type) => {
        if (type === DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_COST_AND_BUDGET) {
            return t('client_panel.daily_limit_scope_campaign_both', 'por campaña (presupuesto y consumo)');
        }
        if (type === DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_COST) {
            return t('client_panel.daily_limit_scope_campaign_cost', 'por consumo de campaña');
        }
        if (type === DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_BUDGET) {
            return t('client_panel.daily_limit_scope_campaign', 'por presupuesto de campaña');
        }
        if (type === DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_ONLY_COST) {
            return t('client_panel.daily_limit_scope_cost', 'por consumo');
        }
        return t('client_panel.daily_limit_scope_account', 'por cuenta');
    };

    // Qué mira exactamente el límite configurado. Es la diferencia que el usuario no puede deducir
    // del monto, y la que decide si la alarma le va a servir o a molestar.
    const scopeExplanation = (type) => {
        if (type === DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_COST_AND_BUDGET) {
            return t('client_panel.daily_limit_explain_campaign_both',
                'Salta cuando UNA campaña de esta cuenta queda configurada con un presupuesto diario mayor a ese monto, o cuando gasta más que ese monto en el día.');
        }
        if (type === DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_COST) {
            return t('client_panel.daily_limit_explain_campaign_cost',
                'Salta cuando UNA campaña de esta cuenta gasta en el día más que ese monto.');
        }
        if (type === DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_BUDGET) {
            return t('client_panel.daily_limit_explain_campaign',
                'Salta cuando UNA campaña de esta cuenta queda configurada con un presupuesto diario mayor a ese monto.');
        }
        if (type === DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_ONLY_COST) {
            return t('client_panel.daily_limit_explain_cost',
                'Salta cuando lo que la cuenta gastó en el día supera ese monto.');
        }
        return t('client_panel.daily_limit_explain_account',
            'Salta cuando el gasto del día —o la suma de los presupuestos diarios de sus campañas— supera ese monto.');
    };

    // El contexto para elegir un monto: lo que la cuenta tiene cargado hoy en la plataforma.
    const currentBudgetHint = (account) => {
        const daily = formatExactMoney(account.currency_code, account.daily_budget);
        if (!daily) return null;
        const maxCampaign = formatExactMoney(account.currency_code, account.max_campaign_daily_budget);
        if (maxCampaign) {
            return t('client_panel.daily_limit_current_budget_with_max',
                'Hoy esta cuenta tiene {{daily}} de presupuesto diario cargado (la campaña más alta, {{max}}).',
                {daily, max: maxCampaign, interpolation: {escapeValue: false}});
        }
        return t('client_panel.daily_limit_current_budget',
            'Hoy esta cuenta tiene {{daily}} de presupuesto diario cargado.',
            {daily, interpolation: {escapeValue: false}});
    };

    // El pie con el presupuesto cargado sólo tiene sentido si la alarma MIRA el presupuesto. Debajo
    // de un límite por consumo decía «Hoy esta cuenta tiene ARS 0 de presupuesto diario cargado» —
    // el dato equivocado, y justo la confusión que este tipo de límite existe para evitar. Sin
    // límite configurado sí se muestra: ahí sirve para elegir el monto.
    const shouldShowBudgetHint = (account, configured) => (
        !configured || DAILY_LIMIT_TYPES_WATCHING_BUDGET.includes(account.daily_limit_type)
    );

    const renderTooltip = (account, configured) => (
        <span className="info-icon-container pnl-dl__info">
            <Info size={13} weight="bold"/>
            <span className="warning-tooltip -rich -below-right">
                <span className="warning-tooltip-body">
                    <span className="pnl-dl__tip">
                        <span className="pnl-dl__tip-title">
                            {t('client_panel.daily_limit_what_title', '¿Qué es el límite diario?')}
                        </span>
                        <span className="pnl-dl__tip-text">
                            {t('client_panel.daily_limit_what',
                                'Una alarma de gasto: Dashbo te avisa por email el mismo día en que la cuenta '
                                + 'se pasa del monto que definas. No frena la inversión ni toca la campaña — '
                                + 'sirve para enterarte de un error de carga antes de que se lleve el presupuesto del mes.')}
                        </span>
                        {configured && (
                            <span className="pnl-dl__tip-text">{scopeExplanation(account.daily_limit_type)}</span>
                        )}
                        {shouldShowBudgetHint(account, configured) && currentBudgetHint(account) && (
                            <span className="pnl-dl__tip-text -muted">{currentBudgetHint(account)}</span>
                        )}
                        {exceededToday(account) && account.daily_limit_last_exceed_notification_at && (
                            <span className="pnl-dl__tip-text -warn">
                                {t('client_panel.daily_limit_exceeded_at', 'Se pasó hoy, a las {{at}}.',
                                    {at: account.daily_limit_last_exceed_notification_at,
                                        interpolation: {escapeValue: false}})}
                            </span>
                        )}
                    </span>
                </span>
            </span>
        </span>
    );

    const renderPill = (account) => {
        const configured = hasDailyLimit(account);
        if (!configured) {
            return (
                <span className="pnl-dl -none">
                    <ShieldWarning size={13} weight="bold"/>
                    {t('client_panel.daily_limit_none', 'Sin límite diario')}
                    {renderTooltip(account, false)}
                </span>
            );
        }

        const exceeded = exceededToday(account);
        const amount = formatExactMoney(account.daily_limit_currency, account.daily_limit_value);
        return (
            <span className={`pnl-dl ${exceeded ? '-exceeded' : '-set'}`}>
                {exceeded ? <Warning size={13} weight="fill"/> : <ShieldCheck size={13} weight="bold"/>}
                <span className="pnl-dl__amount">{amount}</span>
                <span className="pnl-dl__scope">{scopeLabel(account.daily_limit_type)}</span>
                {exceeded && (
                    <span className="pnl-dl__flag">
                        {t('client_panel.daily_limit_exceeded', 'se pasó hoy')}
                    </span>
                )}
                {renderTooltip(account, true)}
            </span>
        );
    };

    /**
     * Sólo aparece cuando la cuenta NO es accesible.
     *
     * El mockup le da un tilde verde a cada cuenta que anda; acá no se pinta nada. Una columna que
     * dice "todo bien" en el 95% de las filas entrena al ojo a saltearla, y es justo la señal que
     * tiene que resaltar el día que una cuenta se cae.
     *
     * **El verbo depende del canal Y del motivo.** Sólo paid media y GA4 se recuperan con el OAuth
     * de la pantalla de conexiones (mismo gate que la ficha clásica): una tienda o una hoja caídas
     * muestran el estado sin prometer un "Reconectar" que en su fila no existe. Y los dos problemas
     * de un CSV —datos mal formados (`SHEET_DATA_INVALID`) y planilla ilegible
     * (`SHEET_NOT_READABLE`)— se arreglan EN LA PLANILLA o en su propia conexión: SHEET está tipado
     * como paid media, así que sin ese segundo gate esta fila ofrecía reconectar para un problema
     * que reconectar no toca — mismo criterio que `BusinessUnitAdAccountNameCell._canReconnect`.
     *
     * `error_kind` NO se muestra: puede nombrar la credencial de otra agencia (aislamiento
     * cross-agency). Acá se lo LEE para decidir el verbo, que es otra cosa. El detalle de por qué se
     * cayó ya está en la pantalla de conexiones.
     */
    const renderAccess = (account) => {
        if (account.has_access !== false) return null;
        if (!supportsReconnect(account.channel) || isFixedInTheSpreadsheet(account.error_kind)) {
            return (
                <span className="pnl-acct__broken -flat">
                    <Warning size={12} weight="fill"/>
                    {t('client_panel.account_no_access', 'Sin acceso')}
                </span>
            );
        }
        return (
            <Link className="pnl-acct__broken" to={`/client/${account.agency_client_id}/conections`}>
                <ArrowsClockwise size={12} weight="bold"/>
                {t('client_panel.account_reconnect', 'Sin acceso — reconectar')}
            </Link>
        );
    };

    /**
     * Qué le lee Dashbo a esta cuenta: su moneda y el evento que cuenta como conversión.
     *
     * La moneda importa porque es la de los importes de esa fuente, y un cliente puede tener cuentas
     * en dos monedas —de ahí salen los "—" del gasto total en el listado—. El evento, porque define
     * el CPA y las conversiones de todo lo que se mide sobre esa cuenta: hasta acá había que abrir el
     * modal de conversión para saberlo.
     *
     * El texto lo arma `accountConversionLabel`, el MISMO helper que usa ese modal: si el modal
     * dijera "Compras" y esta fila otra cosa, el usuario no sabría a cuál creerle.
     *
     * Una excepción, a propósito: el modal pide el catálogo de la cuenta y nombra las conversiones
     * personalizadas (todas las de Google); esta fila NO lo pide —sería un GET por cuenta cada vez
     * que se pinta el Panel— y las resume contadas («3 conversiones personalizadas»). Dicen lo
     * mismo con distinto detalle, nunca cosas distintas.
     */
    const renderSourceMeta = (account) => {
        // `channelInfo` y no `CHANNEL_INFO_BY_CHANNEL[account.channel]`: normaliza META→FACEBOOK,
        // que es lo que hace `accountConversionLabel` dos líneas más abajo. Con los dos criterios
        // conviviendo, el día que el presenter emita 'META' esta fila decidiría "no tiene eventos"
        // mientras el helper sí los resuelve.
        const accountInfo = channelInfo(account) || {};
        // Un canal sin eventos de conversión (una hoja de cálculo) no dice nada acá: "este canal no
        // maneja eventos" es una aclaración de modal, no un dato de una fila.
        const conversion = accountInfo.hasActions ? accountConversionLabel(account, t) : null;
        const configured = !!account.use_all_primary_conversions
            || (account.selected_actions || []).length > 0;

        return (
            <span className="pnl-acct__meta">
                {account.currency_code && (
                    <span className="pnl-acct__cur">{account.currency_code}</span>
                )}
                {conversion && (
                    <span className={`pnl-acct__conv ${configured ? '' : '-none'}`}>{conversion}</span>
                )}
                {renderAccess(account)}
            </span>
        );
    };

    return (
        <div className="pnl-accts">
            {accounts.map((account, index) => (
                <div className="pnl-acct" key={account.id}>
                    <span className="pnl-acct__id">
                        <span className="pnl-acct__name">{account.name || account.account_id}</span>
                        {account.name && <span className="pnl-acct__num">{account.account_id}</span>}
                    </span>
                    {renderSourceMeta(account)}
                    {limitApplies && (
                        <span className="pnl-acct__limit">
                            {renderPill(account)}
                            {/* El modal de la ficha clásica, tal cual: el formulario del límite diario
                                ya existe y duplicarlo daría dos lugares donde arreglar el mismo bug.
                                La guía «Mostrarme cómo» del límite diario (Aprende Dashbo) apunta al
                                LÁPIZ de la primera cuenta: con el renglón entero, un clic en la pastilla
                                terminaba la guía sin abrir nada (review C-apr-guias-06). Cada canal con
                                límite marca la suya y la guía toma la primera del DOM, siempre la misma. */}
                            <EditClientAdAccountModalButton
                                clientAdAccount={account}
                                clientId={account.agency_client_id}
                                clientName={account.agency_client_name}
                                onClientAdAccountEdited={onEdited}
                                guideAnchor={index === 0 ? anchorAttr('panel_daily_limit') : undefined}
                            />
                        </span>
                    )}
                    {/* Va ADENTRO de la fila de la cuenta y ocupa su propio renglón (el CSS le da
                        el ancho completo): el progreso es de ESA cuenta, y colgado del canal se
                        perdería la referencia en cuanto el canal tiene dos. */}
                    <HistoricalSyncRow sync={syncOf(account, syncByAdAccountId)} channel={account.channel}/>
                </div>
            ))}
        </div>
    );
}
